// Pure (DOM-free) readers for the SymEnergy portal's contracted-place roster and statement tables.
// The same roster is rendered on /demands, /meter_readings and /amounts, so place identity and
// contract status come from that markup — never from a literal place id baked into the UI.

import { ContractedPlace, ContractStatus } from '../types/energy';

export type { ContractStatus };

// Portal <select> codes seen on /demands: 1=申込手続中, 2=契約中, 5=廃止処理中, 9=契約終了
export const CONTRACT_STATUSES: ContractStatus[] = ['契約中', '契約終了', '申込手続中', '廃止処理中'];

export type ContractStatusLabelKey =
  | 'statusActive'
  | 'statusEnded'
  | 'statusPending'
  | 'statusCancelled';

const STATUS_LABEL_KEYS: Record<ContractStatus, ContractStatusLabelKey> = {
  '契約中': 'statusActive',
  '契約終了': 'statusEnded',
  '申込手続中': 'statusPending',
  '廃止処理中': 'statusCancelled'
};

/**
 * Localized contract status text. `labels` is the active translation tree, so the four portal
 * states stay distinguishable in EN instead of collapsing into "Active"/"Ended".
 */
export function contractStatusLabel(
  status: ContractStatus | undefined,
  labels: Record<ContractStatusLabelKey, string>
): string {
  if (!status) return '';
  return labels[STATUS_LABEL_KEYS[status]] ?? '';
}

export interface RosterRow {
  /** Cell text per column, in document order. */
  cells: string[];
  /** href of the row's first link, which carries the numeric place id. */
  href?: string;
}

const FULLWIDTH_DIGITS = /[\uFF10-\uFF19]/g;

export function normalizeFullWidthDigits(value: string): string {
  return value.replace(FULLWIDTH_DIGITS, (ch) => String.fromCharCode(ch.charCodeAt(0) - 0xfee0));
}

export function contractStatusFromText(text: string): ContractStatus | null {
  const value = (text || '').trim();
  return CONTRACT_STATUSES.find((status) => value.includes(status)) ?? null;
}

/**
 * The roster link (/meter_readings/318949, /demands/318949) is the authoritative numeric id.
 * The printed ご使用場所番号 is prefixed (J0318949) and is only a fallback.
 */
export function placeIdFromRosterRow(href: string | undefined, demandNumber: string): string {
  const fromHref = /(?:meter_readings|demands|amounts)\/([0-9]{5,})/i.exec(href || '');
  if (fromHref) return fromHref[1];
  const fromDemand = /^J0*([0-9]{5,})$/i.exec((demandNumber || '').trim());
  return fromDemand ? fromDemand[1] : '';
}

const ROSTER_COLUMNS: { key: keyof ContractedPlace; header: string }[] = [
  { key: 'demandNumber', header: 'ご使用場所番号' },
  { key: 'status', header: 'ステータス' },
  { key: 'displayName', header: 'ご使用場所表示名' },
  { key: 'contractName', header: 'ご使用場所名義' },
  { key: 'planName', header: 'プラン' },
  { key: 'address', header: '住所' },
  { key: 'paymentGroup', header: '支払グループ' }
];

export function isPlaceRosterHeader(headers: string[]): boolean {
  return headers.some((h) => h.includes('ご使用場所番号')) && headers.some((h) => h.includes('ステータス'));
}

/** Maps the roster table into per-place records, matched by header text (column order varies). */
export function mapPlaceRosterRows(headers: string[], rows: RosterRow[]): Partial<ContractedPlace>[] {
  if (!isPlaceRosterHeader(headers)) return [];

  const columnIndex = ROSTER_COLUMNS.map(({ header }) => headers.findIndex((h) => h.includes(header)));
  const places: Partial<ContractedPlace>[] = [];

  for (const row of rows) {
    const valueAt = (column: number) => {
      const idx = columnIndex[column];
      return idx >= 0 ? (row.cells[idx] || '').trim() : '';
    };

    const demandNumber = valueAt(0);
    const status = contractStatusFromText(valueAt(1));
    const id = placeIdFromRosterRow(row.href, demandNumber);
    // A row without both an id and a real status is a header/placeholder row, not a contract.
    if (!id || !status) continue;

    const place: Partial<ContractedPlace> = { id, demandNumber: demandNumber || id, status };
    const displayName = valueAt(2);
    const contractName = valueAt(3);
    const planName = valueAt(4);
    const address = valueAt(5);
    const paymentGroup = valueAt(6);
    if (displayName) place.displayName = displayName;
    if (contractName) place.contractName = contractName;
    if (planName) place.planName = planName;
    if (address) place.address = address;
    if (paymentGroup) place.paymentGroup = paymentGroup;
    places.push(place);
  }

  return places;
}

/**
 * Portal place labels start with a full-width ordinal (２＿アハマド シャヒル), which is what the
 * EN UI needs for "Location 2" — so the number is read from the label instead of a fixed id map.
 */
export function placeOrdinal(
  place: Pick<ContractedPlace, 'displayName' | 'demandNumber'>,
  fallbackIndex?: number
): number | null {
  const label = normalizeFullWidthDigits(place.displayName || '');
  const leading = /^\s*([0-9]{1,2})\D/.exec(label) || /^\s*([0-9]{1,2})$/.exec(label);
  if (leading) return Number(leading[1]);
  if (typeof fallbackIndex === 'number' && fallbackIndex >= 0) return fallbackIndex + 1;
  return null;
}

/** Header of the portal's confirmed-statement ledger (/amounts/{id} and /meter_readings/{id}). */
export function isStatementLedgerHeader(headers: string[]): boolean {
  return headers.some((h) => h.includes('請求年月')) || headers.some((h) => h.includes('ご利用年月'));
}

const BILLING_MONTH_CELL = /^\s*(20[0-9]{2})\s*年\s*([0-9]{1,2})\s*月|^\s*(20[0-9]{2})[/-]([0-9]{1,2})\s*$/;

/**
 * Counts only rows that really carry a 請求年月. The portal renders a single colspan placeholder
 * cell (表示するデータが存在しません) when nothing is confirmed, which must not count as a row.
 */
export function countConfirmedStatements(rows: string[][]): number {
  return rows.filter((row) => BILLING_MONTH_CELL.test(row[0] || '')).length;
}

/**
 * Which location the portal itself treats as current: the roster row marked 契約中.
 * The writer keys telemetry by this place id and the reader filters by it, so both call
 * this one rule instead of each guessing - disagreeing reads back an empty dashboard.
 */
export function activeRosterPlaceId(
  places: ReadonlyArray<{ id: string; status?: ContractStatus }>
): string {
  return places.find((place) => place.status === '契約中')?.id || '';
}

/** One row of the portal's confirmed-statement ledger (the 請求年月 table on /amounts/{id}). */
export interface StatementLedgerEntry {
  year: number;
  month: number;
  readingDate: string;
  daysCount: number;
  kwh: number;
  dailyAvgKwh: number;
  amountInclTax: number;
  amountExclLevy: number;
}

const ledgerNumber = (cell: string): number => {
  const value = Number.parseFloat(cell.replace(/,/g, '').replace(/[^0-9.\-]/g, ''));
  return Number.isFinite(value) ? value : 0;
};

/**
 * Maps ledger cells into billing rows, matching columns by header text because the column
 * order differs between /amounts/{id} and /meter_readings/{id}. This table is the ONLY
 * honest source of 検針日 / 使用日数 / 請求金額, so a missing cell reads as 0 and a row without
 * a real 請求年月 is dropped - never estimated.
 */
export function mapStatementLedgerRows(
  headers: string[],
  rows: string[][]
): StatementLedgerEntry[] {
  const normalized = headers.map((h) => h.replace(/\s+/g, ''));
  const find = (pred: (header: string) => boolean) => normalized.findIndex(pred);
  const monthIdx = find((h) => h.includes('請求年月'));
  if (monthIdx < 0) return [];
  const readingIdx = find((h) => h.includes('検針日'));
  const daysIdx = find((h) => h.includes('使用日数'));
  const avgIdx = find((h) => h.includes('平均'));
  const kwhIdx = find((h) => h.includes('使用量') && !h.includes('平均'));
  const inclIdx = find((h) => h.includes('税込'));
  const exclIdx = find((h) => h.includes('燃調') || h.includes('再エネ'));

  const entries: StatementLedgerEntry[] = [];
  for (const row of rows) {
    const cell = (idx: number) => (idx < 0 ? '' : (row[idx] || '').replace(/\s+/g, ''));
    const stamp = BILLING_MONTH_CELL.exec(cell(monthIdx));
    if (!stamp) continue;
    entries.push({
      year: Number(stamp[1] || stamp[3]),
      month: Number(stamp[2] || stamp[4]),
      readingDate: cell(readingIdx),
      daysCount: ledgerNumber(cell(daysIdx)),
      kwh: ledgerNumber(cell(kwhIdx)),
      dailyAvgKwh: ledgerNumber(cell(avgIdx)),
      amountInclTax: ledgerNumber(cell(inclIdx)),
      amountExclLevy: ledgerNumber(cell(exclIdx))
    });
  }
  return entries;
}
